import { useEffect, useEffectEvent, type RefObject } from "react";
import type {
  CaptureStart,
  CaptureStartAnswer,
  CaptureStatus,
  DesktopBridge,
  IslandRect,
  IslandShortcut,
  IslandStatus,
  Shareable,
} from "@typedoc/contracts";
import type { CaptureGrant, CaptureShare } from "@typedoc/contracts/capture";
import { isRecordingReady, islandShortcutKeys } from "@typedoc/contracts/desktop";
import { queryClient } from "../query-client";

/**
 * The desktop app's recording capability (bridge versions 3 and 4, ENG-1016 and ENG-1024), as the
 * web uses it. Every
 * piece is checked by its function, as `openSignIn` is: an installed shell may be older than the
 * page it loads, and a browser has none of it.
 */

function currentDesktop(): unknown {
  return globalThis.window?.desktop;
}

/** One member of the bridge, when it is a function this shell has. */
function member<K extends keyof DesktopBridge>(
  desktop: unknown,
  key: K,
): NonNullable<DesktopBridge[K]> | undefined {
  const value = (desktop as Partial<DesktopBridge> | undefined)?.[key];
  return typeof value === "function" ? value : undefined;
}

/** The system the shell runs on (`process.platform`), or "" in a browser. */
function desktopPlatform(desktop: unknown): string {
  const platform = (desktop as Partial<DesktopBridge> | undefined)?.platform;
  return typeof platform === "string" ? platform : "";
}

/**
 * The keys of one of the island's shortcuts as they are drawn: ⌥ and ⇧ on a Mac (and in a
 * browser, where the island is a preview), their names on Windows, where Ctrl joins them.
 */
export function islandShortcutLabel(
  shortcut: IslandShortcut,
  desktop: unknown = currentDesktop(),
): readonly string[] {
  const platform = desktopPlatform(desktop);
  const keys = islandShortcutKeys(platform)[shortcut];
  if (platform === "win32" || platform === "linux") return keys;
  return keys.map((key) => (key === "Alt" ? "⌥" : key === "Shift" ? "⇧" : key));
}

/**
 * Whether the island can hear the person and play Abacor's voice here (ENG-1017). A browser asks
 * the person for the mic itself. A desktop shell lets the island's window use the mic from bridge
 * version 8; an older one refuses every device to hosted pages, so its recordings ask in text.
 */
export function desktopIslandVoice(desktop: unknown = currentDesktop()): boolean {
  if (desktop === undefined || desktop === null) return true;
  const version = (desktop as Partial<DesktopBridge>).bridgeVersion;
  return typeof version === "number" && version >= 8;
}

/** Whether the island runs in the desktop app's unpacked development build (`development`). */
export function desktopDevelopment(desktop: unknown = currentDesktop()): boolean {
  return (desktop as Partial<DesktopBridge> | undefined)?.development === true;
}

/** The shell's way to open the island on a recording, where it has one. */
export function desktopShowIsland(desktop: unknown = currentDesktop()) {
  return member(desktop, "showIsland");
}

/**
 * Whether a recording can be started here: the shell can show it only once the screen and the mic
 * are allowed. When they are not, the shell's setup opens for the person to allow them and the
 * answer is no, so no recording starts that nobody can see. A shell that cannot say, or does not
 * answer, is left to decide when the island opens.
 */
export async function desktopReadyToRecord(desktop: unknown = currentDesktop()): Promise<boolean> {
  const ask = member(desktop, "recordingPermissions");
  if (!ask) return true;
  const permissions = await ask().catch(() => null);
  if (!permissions) return true;
  const ready = isRecordingReady(permissions, desktopPlatform(desktop));
  if (!ready) member(desktop, "openRecordingSetup")?.();
  return ready;
}

/** The tray's Start a recording, delivered to this page while `onStart` is set. */
export function useDesktopStartRecording(onStart: () => void, desktop: unknown = currentDesktop()) {
  const listen = member(desktop, "onStartRecording");
  const started = useEffectEvent(onStart);
  useEffect(() => listen?.(() => started()), [listen]);
}

/**
 * Tells the shell where the island is drawn, so clicks anywhere else pass through its window to
 * the app underneath. Follows the island as it grows and shrinks, and as the page swaps one state
 * for another (loading, the island, a problem). Each `recording` is reported afresh, even drawn
 * the same as the last: the shell shows a kept island's window only once its next one is drawn.
 */
export function useIslandShape(
  page: RefObject<HTMLElement | null>,
  recording?: string,
  desktop = currentDesktop(),
) {
  const setShape = member(desktop, "setIslandShape");
  useEffect(() => {
    const root = page.current;
    if (!setShape || !root) return;
    let frame: ReturnType<typeof setTimeout> | undefined;
    let last = "";
    const report = () => {
      clearTimeout(frame);
      // A task, not an animation frame: a kept island draws its next recording while its window
      // is still hidden, where no frame comes until the window shows, which waits for this.
      frame = setTimeout(() => {
        const rects: IslandRect[] = [...root.querySelectorAll(":scope > .island")].map((el) => {
          const r = el.getBoundingClientRect();
          return { x: r.x, y: r.y, width: r.width, height: r.height };
        });
        const key = `${recording ?? ""} ${JSON.stringify(rects)}`;
        if (key === last) return;
        last = key;
        setShape(rects);
      });
    };
    const sizes = new ResizeObserver(report);
    const watch = () => {
      sizes.disconnect();
      for (const el of root.querySelectorAll(":scope > .island")) sizes.observe(el);
      report();
    };
    const swaps = new MutationObserver(watch);
    swaps.observe(root, { childList: true });
    watch();
    return () => {
      clearTimeout(frame);
      sizes.disconnect();
      swaps.disconnect();
    };
  }, [page, setShape, recording]);
}

/** Tells the shell how the island's recording stands, when that changes, for its tray. */
export function useIslandStatus(status: IslandStatus, desktop = currentDesktop()) {
  const setStatus = member(desktop, "setIslandStatus");
  const { mode, notice } = status;
  useEffect(() => {
    setStatus?.({ mode, notice });
  }, [setStatus, mode, notice]);
}

/** The shell's global shortcuts, while this island is open. */
export function useIslandShortcuts(
  onShortcut: (shortcut: IslandShortcut) => void,
  desktop = currentDesktop(),
) {
  const listen = member(desktop, "onIslandShortcut");
  const pressed = useEffectEvent(onShortcut);
  useEffect(() => listen?.((shortcut) => pressed(shortcut)), [listen]);
}

/**
 * Readies the island's window, hidden, once the workspace has settled (bridge version 10), so a
 * recording's island shows at once instead of loading the web app first. It waits for the page
 * the person is looking at: the island is not what they are waiting for. A shell without it opens
 * the island as before.
 */
export function useDesktopPrepareIsland(
  desktop: unknown = currentDesktop(),
  page: SettlingPage = BROWSER_PAGE,
) {
  const prepare = member(desktop, "prepareIsland");
  useEffect(() => (prepare ? whenSettled(page, () => prepare()) : undefined), [prepare, page]);
}

/**
 * Readies the island's window at once, for when the person heads for a recording (the pointer on
 * Show screen, say) before the workspace has settled; the shell does nothing when it is ready.
 * It passes nothing on, so it can be an event handler.
 */
export function desktopPrepareIsland(desktop: unknown = currentDesktop()) {
  const prepare = member(desktop, "prepareIsland");
  return prepare && (() => prepare());
}

/** What `whenSettled` watches of the page, so a test can play it. */
export interface SettlingPage {
  /** Calls `then` once the page has loaded, at once if it has; answers a way to stop waiting. */
  loaded(then: () => void): () => void;
  /** Whether the app is fetching anything. */
  fetching(): boolean;
  /** Calls `changed` whenever what the app fetches may have changed; answers a way to stop. */
  onFetching(changed: () => void): () => void;
  /** Calls `then` when the browser is next idle; answers a way to cancel it. */
  idle(then: () => void): () => void;
}

/**
 * Calls `then` once the page has settled: loaded, nothing being fetched, and the browser idle. A
 * fetch that starts while it waits for the idle moment sends it back to waiting. Answers a way to
 * stop.
 */
export function whenSettled(page: SettlingPage, then: () => void): () => void {
  let stopped = false;
  let cancel = () => {};
  const check = () => {
    if (stopped) return;
    if (page.fetching()) quiet();
    else then();
  };
  const quiet = () => {
    if (!page.fetching()) {
      cancel = page.idle(check);
      return;
    }
    const stop = page.onFetching(() => {
      if (page.fetching()) return;
      stop();
      cancel = page.idle(check);
    });
    cancel = stop;
  };
  const stopLoad = page.loaded(quiet);
  return () => {
    stopped = true;
    stopLoad();
    cancel();
  };
}

const BROWSER_PAGE: SettlingPage = {
  loaded(then) {
    if (document.readyState === "complete") {
      then();
      return () => {};
    }
    window.addEventListener("load", then, { once: true });
    return () => window.removeEventListener("load", then);
  },
  fetching: () => queryClient.isFetching() > 0,
  onFetching: (changed) => queryClient.getQueryCache().subscribe(changed),
  idle(then) {
    const id = requestIdleCallback(() => then());
    return () => cancelIdleCallback(id);
  },
};

/** Whether the shell keeps the island's page between recordings and says which to show. */
export function desktopKeepsIsland(desktop: unknown = currentDesktop()): boolean {
  return member(desktop, "onIslandRecording") !== undefined;
}

/**
 * The recording the shell says the island shows (bridge version 10): the island's page is kept
 * between recordings and told the next one in place. `onRecording` gets the recording now, then
 * each change, null while the island waits, hidden. Nothing where the shell keeps no page.
 */
export function useIslandRecording(
  onRecording: ((recordingId: string | null) => void) | null,
  desktop: unknown = currentDesktop(),
) {
  const listen = member(desktop, "onIslandRecording");
  const told = useEffectEvent((recordingId: string | null) => onRecording?.(recordingId));
  const on = onRecording !== null;
  useEffect(() => (on ? listen?.((recordingId) => told(recordingId)) : undefined), [listen, on]);
}

/**
 * Closes the island's window, where the shell has one to close. It passes nothing on, so it can be
 * a click handler: the bridge cannot clone a click's event, and would throw instead of closing.
 */
export function desktopCloseIsland(desktop: unknown = currentDesktop()) {
  const close = member(desktop, "closeIsland");
  return close && (() => close());
}

/** What Abacor sees, in a shell that lets the person choose (bridge version 5, ENG-1031). */
export type DesktopSharing = {
  shareable: () => Promise<Shareable | null>;
  share: (share: CaptureShare) => void;
  keepOut: (bundleId: string) => void;
  /** What is shared now (null: no capture is going), then each change; the answer stops. */
  onStatus: (listener: (status: CaptureStatus | null) => void) => () => void;
  /**
   * Card fields are covered as well as password fields (bridge version 6, ENG-1026). A version 5
   * shell lets the person choose but covers password fields only, so the island promises only that.
   */
  cardFields: boolean;
};

/** The shell's screen capture for the island's recording (bridge version 4, ENG-1024). */
export type DesktopCapture = {
  /** Answers null when the shell refused the call. */
  start: (start: CaptureStart) => Promise<CaptureStartAnswer | null>;
  grant: (grant: CaptureGrant) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /** The person started talking (bridge version 7, ENG-1025); absent from an older shell. */
  speech?: () => void;
  /** Present in a shell that lets the person choose what is shared (bridge version 5). */
  sharing?: DesktopSharing;
};

/**
 * The shell's screen capture, only when it has every part of it: an older shell, or a browser,
 * records as before, without screens.
 */
export function desktopCapture(desktop: unknown = currentDesktop()): DesktopCapture | undefined {
  const start = member(desktop, "startCapture");
  const grant = member(desktop, "setCaptureGrant");
  const pause = member(desktop, "pauseCapture");
  const resume = member(desktop, "resumeCapture");
  const stop = member(desktop, "stopCapture");
  if (!start || !grant || !pause || !resume || !stop) return undefined;
  const shareable = member(desktop, "shareableContent");
  const share = member(desktop, "setCaptureShare");
  const keepOut = member(desktop, "keepOutOfCapture");
  const onStatus = member(desktop, "onCaptureStatus");
  const version = (desktop as { bridgeVersion?: unknown } | undefined)?.bridgeVersion;
  const cardFields = typeof version === "number" && version >= 6;
  const sharing =
    shareable && share && keepOut && onStatus
      ? { shareable, share, keepOut, onStatus, cardFields }
      : undefined;
  const speech = member(desktop, "captureSpeech");
  return {
    start,
    grant,
    pause,
    resume,
    stop,
    ...(speech ? { speech } : {}),
    ...(sharing ? { sharing } : {}),
  };
}
